import type { AddressDto } from "@fp/admin-api-client/types/addressDto";
import { Input } from "@/components/base/input/input";
import { StateSelect } from "@/components/base/select/state-select";
import { AddressAutocompleteInput } from "./address-autocomplete-input";

export interface AddressFieldGroupValue {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

export type AddressFieldKey = keyof AddressFieldGroupValue;

export interface AddressFieldGroupProps {
  value: AddressFieldGroupValue;
  onChange: (value: AddressFieldGroupValue) => void;
  fields?: AddressFieldKey[];
  disabled?: boolean;
}

const defaultFields: AddressFieldKey[] = ["addressLine1", "addressLine2", "city", "state", "zip"];

export const AddressFieldGroup = ({ value, onChange, fields = defaultFields, disabled }: AddressFieldGroupProps) => {
  const setField = (key: AddressFieldKey, next: string) => onChange({ ...value, [key]: next });

  const handleAddressSelected = (address: AddressDto) => onChange({
    ...value,
    addressLine1: address.addressLine1 ?? value.addressLine1,
    city: address.city ?? value.city,
    state: address.state ?? value.state,
    zip: address.zip ?? value.zip,
  });

  const showCity = fields.includes("city");
  const showState = fields.includes("state");
  const showZip = fields.includes("zip");
  const showCityStateZipRow = showCity || showState || showZip;

  return (
    <div className="flex flex-col gap-4">
      {fields.includes("addressLine1") && (
        <AddressAutocompleteInput value={value.addressLine1 ?? ""} onChange={(v) => setField("addressLine1", v)} onAddressSelected={handleAddressSelected} label="Address Line 1" />
      )}
      {fields.includes("addressLine2") && (
        <Input label="Address Line 2" value={value.addressLine2 ?? ""} onChange={(v) => setField("addressLine2", v)} isDisabled={disabled} />
      )}
      {showCityStateZipRow && (
        <div className="flex gap-4">
          {showCity && (
            <div className="flex-1">
              <Input label="City" value={value.city ?? ""} onChange={(v) => setField("city", v)} isDisabled={disabled} />
            </div>
          )}
          {showState && (
            <div className="flex-1">
              <StateSelect label="State" size="sm" value={value.state ?? ""} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setField("state", e.target.value)} disabled={disabled} />
            </div>
          )}
          {showZip && (
            <div className="flex-1">
              <Input label="Zip" value={value.zip ?? ""} onChange={(v) => setField("zip", v)} isDisabled={disabled} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};